/** @format */
import React, { useCallback, useEffect, useState } from 'react';
import CustomSmallTextButton from '../../../components/Buttons/CustomSmallTextButton';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { configDefault } from '../../../core/config';
import { useDispatch } from 'react-redux';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import InputFormRest from '../../../components/Form/InputFormRest';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { tariffConstants } from '../../../modules/tariffs/actions';
import i18n from '../../../core/i18n';
import SelectFromRest from '../../../components/Form/SelectFromRest';
import { platformResellerConstants } from '../../../modules/platformreseller/actions';
import {
    PlatformResellerCollectionInterface
} from '../../../interfaces/collections/PlatformResellerCollectionInterface';

interface PlatformMsisdnRangePoolInterface {
    id?: number;
    rangeStart: string;
    rangeEnd: string;
    platformResellerName: string | null;
}

interface PlatformMsisdnRangePoolAddProps {
    classes: any;
    pool?: PlatformMsisdnRangePoolInterface;
    editMode?: boolean;
    onSaveSuccess?: () => void;
}

const PlatformMsisdnRangePoolAdd = (props: PlatformMsisdnRangePoolAddProps): React.ReactElement => {
    const dispatch = useDispatch();

    const URI = '/platform_msisdn_range_pools';

    const [entityStatus, setEntityStatus] = useState<PlatformMsisdnRangePoolInterface>({
        rangeStart: '',
        rangeEnd: '',
        platformResellerName: null,
    });

    const [entity, setEntity] = useState<PlatformMsisdnRangePoolInterface>(
        props.pool
            ? {
                  id: props.pool.id,
                  rangeStart: props.pool.rangeStart,
                  rangeEnd: props.pool.rangeEnd,
                  platformResellerName: props.pool.platformResellerName,
              }
            : {
                  rangeStart: '',
                  rangeEnd: '',
                  platformResellerName: null,
              }
    );
    const PLATFORM_RESELLER_URI = '/platform_resellers';

    const [platformResellerList, setPlatformResellerList] = useState<PlatformResellerCollectionInterface | null>(null);
    const [isLoadingResellers, setIsLoadingResellers] = useState<boolean>(false);

    const loadPlatformResellers = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PLATFORM_RESELLER_URI, configDefault.PLATFORM_API_PREFIX);
            }

            setIsLoadingResellers(true);
            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    setPlatformResellerList(data);
                    setIsLoadingResellers(false);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );


    useEffect((): void => {
        loadPlatformResellers();
    }, [loadPlatformResellers]);

    const savePool = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        if (props.editMode) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${URI}/${entity.id}`, configDefault.PLATFORM_API_PREFIX)),
                tariffConstants.EDIT,
                entity,
                {
                    onSuccess: (pool: PlatformMsisdnRangePoolInterface): void => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                        if (props.onSaveSuccess) {
                            props.onSaveSuccess();
                        }
                    },
                    onError: (error: any): void => {
                        if (error.message.length > 0 && error.message[0].message){
                            NotificationHelper.notifyError(error.message[0].message, dispatch);
                        }else {
                            NotificationHelper.notifyError('prefixes:notify.error', dispatch);
                        }                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        } else {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX)),
                tariffConstants.ADD,
                entity,
                {
                    onSuccess: (pool: PlatformMsisdnRangePoolInterface): void => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                        if (props.onSaveSuccess) {
                            props.onSaveSuccess();
                        }
                    },
                    onError: (error: any): void => {
                        if (error.message.length > 0 && error.message[0].message){
                            NotificationHelper.notifyError(error.message[0].message, dispatch);
                        }else {
                            NotificationHelper.notifyError('prefixes:notify.error', dispatch);
                        }
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        }
    };

    const changeInput = (e: any, stateName: string, type: string, require: boolean, maxValue: number, minValue: number): void => {
        // TODO: Add validation (use entityStatus)
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    return (
        <Card>
            {!props.editMode ? <CardHeader trans="pools:h.platform_msisdn_range_pools" /> : null}
            <CardBody>
                {isLoadingResellers ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <GridContainer>
                        <GridItem xs={6}>
                            <GridContainer spacing={2}>
                                <GridItem xs={12}>
                                    <InputFormRest
                                        classes={props.classes}
                                        callback={changeInput}
                                        stateName="rangeStart"
                                        stateObjectState={entityStatus.rangeStart}
                                        typeCallback={FormGeneralHelper.TYPE_TEXT}
                                        typeField={FormGeneralHelper.TYPE_TEXT}
                                        displayFieldName="pools:list.range_start"
                                        required={true}
                                        vMax={20}
                                        vMin={0}
                                        value={entity.rangeStart}
                                    />
                                </GridItem>
                                <GridItem xs={12}>
                                    <InputFormRest
                                        classes={props.classes}
                                        callback={changeInput}
                                        stateName="rangeEnd"
                                        stateObjectState={entityStatus.rangeEnd}
                                        typeCallback={FormGeneralHelper.TYPE_TEXT}
                                        typeField={FormGeneralHelper.TYPE_TEXT}
                                        displayFieldName="pools:list.range_end"
                                        required={true}
                                        vMax={20}
                                        vMin={0}
                                        value={entity.rangeEnd}
                                    />
                                </GridItem>
                                <GridItem xs={12}>
                                    <SelectFromRest
                                        callback={changeInput}
                                        required={true}
                                        addEmpty={false}
                                        restElements={platformResellerList ?? { 'hydra:member': [] }}
                                        stateName={'platformResellerName'}
                                        stateObjectState={'platformResellerName'}
                                        selectedValue={entity.platformResellerName}
                                        propKey="mainname"
                                        propValue="mainname"
                                        displayEmpty={true}
                                        emptyLabel={'services_activation:h.select_missing'}
                                        label={'pools:list.platform_reseller_name'}
                                    />
                                </GridItem>

                                <GridItem xs={12}>
                                    <CustomSmallTextButton label={'general:common.save'} callback={savePool} callbackParam={{}} />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                )}
            </CardBody>
        </Card>
    );
};

export default withStyles(oplAppStyle)(PlatformMsisdnRangePoolAdd);
